'use client';

import { useId, useRef } from 'react';

import { LayoutGroup, motion } from 'framer-motion';
import { Hammer, Inbox, Rocket } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  CheckBadge,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  SPRING_SOFT,
  TypingDots,
  localize,
  useDemoTimeline,
} from './demo-kit';
import { FEATURE_REQUEST_DEMO_SIZE } from './demo-sizes';

/*
 * "Built with you" product board, for dark sections. Client requests arrive
 * in Requested, move to In build, then go Live. Every tick, each card ages
 * by one: ages 0-1 sit in Requested, 2-3 in In build, 4-6 in Live, and the
 * oldest Live card fades out and retires as a new request arrives. Cards keep their
 * identity across columns (layoutId), so moves animate across the board.
 * The cards are real features shipped from client requests.
 */


const DURATIONS = [1700, 1700, 1700, 1700, 1700, 1700, 1700] as const;

const COPY = {
  label: {
    en: 'Animated product board: client feature requests move from Requested to In build to Live, where each shipped feature is marked live for the venue.',
    ar: 'لوحة منتج متحركة: طلبات العملاء تنتقل من الطلبات إلى قيد التطوير ثم إلى متاحة الآن، حيث تُعلَّم كل ميزة مُطلقة بأنها متاحة في المنشأة.',
  },
  board: { en: 'Product board', ar: 'لوحة المنتج' },
  withClients: { en: 'Built with our clients', ar: 'نبنيها مع عملائنا' },
  requested: { en: 'Requested', ar: 'الطلبات' },
  inBuild: { en: 'In build', ar: 'قيد التطوير' },
  live: { en: 'Live', ar: 'متاحة الآن' },
  newRequest: { en: 'New request', ar: 'طلب جديد' },
  building: { en: 'Building', ar: 'جارٍ العمل' },
  liveForYou: { en: 'Live for your venue', ar: 'متاحة في منشأتك' },
} as const;

const FEATURES = [
  {
    text: { en: 'Let us postpone a fixed session more than once', ar: 'تأجيل الجلسة الثابتة أكثر من مرة' },
    area: { en: 'Packages', ar: 'الباقات' },
  },
  {
    text: { en: 'Show coach utilization in reports', ar: 'نسبة إشغال المدربين في التقارير' },
    area: { en: 'Reports', ar: 'التقارير' },
  },
  {
    text: { en: 'Filter the WhatsApp inbox by location', ar: 'تصفية صندوق واتساب حسب الفرع' },
    area: { en: 'WhatsApp', ar: 'واتساب' },
  },
  {
    text: { en: 'Add custom fields to customer exports', ar: 'الحقول المخصصة في تصدير العملاء' },
    area: { en: 'Customers', ar: 'العملاء' },
  },
  {
    text: { en: 'Give each location its own contact number', ar: 'رقم تواصل مستقل لكل فرع' },
    area: { en: 'Bookings', ar: 'الحجوزات' },
  },
  {
    text: { en: 'Create a discount while booking', ar: 'إنشاء خصم أثناء الحجز' },
    area: { en: 'Discounts', ar: 'الخصومات' },
  },
  {
    text: { en: 'Show product photos on the POS', ar: 'صور المنتجات في نقطة البيع' },
    area: { en: 'POS', ar: 'نقطة البيع' },
  },
] as const;

type Stage = 'requested' | 'inBuild' | 'live';

const COLUMNS: { stage: Stage; ages: readonly number[]; dot: string; Icon: typeof Inbox }[] = [
  { stage: 'requested', ages: [0, 1], dot: '#a9c8ff', Icon: Inbox },
  { stage: 'inBuild', ages: [2, 3], dot: '#f2c46d', Icon: Hammer },
  { stage: 'live', ages: [4, 5, 6], dot: '#7fd8b4', Icon: Rocket },
];

const BUILD_PROGRESS: Record<number, string> = { 2: '45%', 3: '88%' };

export function FeatureRequestDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { staticStep: 3 });
  const copy = localize(COPY, locale);
  const groupId = useId();
  const tick = timeline.cycle * DURATIONS.length + timeline.step;

  const columnLabel: Record<Stage, string> = {
    requested: copy.requested,
    inBuild: copy.inBuild,
    live: copy.live,
  };

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <div className="relative flex flex-col overflow-hidden" style={FEATURE_REQUEST_DEMO_SIZE}>
        <div className="flex h-7 items-center justify-between">
          <span className="flex items-center gap-2 font-roboto-mono text-[10px] uppercase tracking-[0.16em] text-white/55">
            <motion.span
              className="h-1.5 w-1.5 rounded-full bg-[#a9c8ff]"
              animate={timeline.isRunning ? { opacity: [1, 0.35, 1] } : { opacity: 1 }}
              transition={timeline.isRunning ? { duration: 2, repeat: Infinity } : { duration: 0 }}
            />
            {copy.board}
          </span>
          <span className="text-[11px] text-white/45">{copy.withClients}</span>
        </div>

        <LayoutGroup id={groupId}>
          <div className="mt-3 grid flex-1 grid-cols-3 gap-4">
            {COLUMNS.map(({ stage, ages, dot, Icon }) => (
              <div
                key={stage}
                className="flex flex-col rounded-[18px] border border-white/[0.08] bg-white/[0.03] p-2.5"
              >
                <div className="mb-2.5 flex items-center gap-2 px-1.5 pt-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
                  <span className="text-[12px] font-medium text-white/85">{columnLabel[stage]}</span>
                  <Icon className="ms-auto h-3.5 w-3.5 text-white/35" strokeWidth={2} />
                </div>

                <div className="flex flex-col gap-2">
                  {ages.map((age) => (
                    <BoardCard
                      key={`card-${tick - age}`}
                      birth={tick - age}
                      age={age}
                      stage={stage}
                      locale={locale}
                      copy={copy}
                      isStatic={timeline.isStatic}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </LayoutGroup>
      </div>
    </DemoStage>
  );
}

function BoardCard({
  birth,
  age,
  stage,
  locale,
  copy,
  isStatic,
}: {
  birth: number;
  age: number;
  stage: Stage;
  locale: DemoProps['locale'];
  copy: { [K in keyof typeof COPY]: string };
  isStatic: boolean;
}) {
  const feature = FEATURES[((birth % FEATURES.length) + FEATURES.length) % FEATURES.length]!;
  const isNew = age === 0;
  const retiring = age === 6;

  return (
    <motion.div
      layoutId={`card-${birth}`}
      className={cn(
        'rounded-[12px] border px-3 py-2.5',
        stage === 'live'
          ? 'border-[#7fd8b4]/20 bg-[#7fd8b4]/[0.07]'
          : 'border-white/[0.09] bg-[#2b2b2b]',
      )}
      initial={
        isNew ? { opacity: 0, y: -12, scale: 0.96 } : retiring ? { opacity: 1 } : false
      }
      animate={{
        opacity: retiring && !isStatic ? [1, 0.55, 0.55, 0] : retiring ? 0.55 : 1,
        y: 0,
        scale: 1,
      }}
      transition={{
        ...SPRING_SOFT,
        // The oldest card fades out during its last tick, so it is already
        // invisible when the next tick unmounts it.
        opacity: retiring
          ? { duration: 1.6, times: [0, 0.2, 0.8, 1] }
          : { duration: 0.35 },
      }}
    >
      <div className="flex items-center gap-1.5">
        <span className="rounded-full bg-white/[0.08] px-1.5 py-[1px] text-[9.5px] text-white/60">
          {feature.area[locale]}
        </span>
        {isNew && (
          <span className="ms-auto flex items-center gap-1 text-[9.5px] text-[#a9c8ff]">
            {copy.newRequest}
            <TypingDots color="#a9c8ff" />
          </span>
        )}
        {stage === 'inBuild' && (
          <span className="ms-auto text-[9.5px] text-[#f2c46d]">{copy.building}</span>
        )}
      </div>

      <p className="mt-1.5 text-[12px] leading-[1.35] text-white/90">
        {isNew
          ? feature.text[locale].split(' ').map((word, index) => (
              <motion.span
                key={`${word}-${index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.08, delay: 0.2 + index * 0.1 }}
              >
                {word}{' '}
              </motion.span>
            ))
          : feature.text[locale]}
      </p>

      {stage === 'inBuild' && (
        <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-white/10">
          <motion.div
            className="h-full rounded-full bg-[#f2c46d]"
            initial={{ width: '12%' }}
            animate={{ width: BUILD_PROGRESS[age] ?? '45%' }}
            transition={{ duration: 1.1, ease: EASE_OUT_EXPO, delay: 0.2 }}
          />
        </div>
      )}

      {stage === 'live' && (
        <span className="mt-2 flex items-center gap-1.5 text-[10px] font-medium text-[#7fd8b4]">
          <CheckBadge show size={14} bg="#2f7a63" delay={age === 4 ? 0.5 : 0} />
          {copy.liveForYou}
        </span>
      )}
    </motion.div>
  );
}
